/* eslint-disable */
import { useState, useCallback } from 'react';
import { useSelector, useDispatch, RootStateOrAny } from 'react-redux';
import * as actions from '~/store/actions';
import { _ } from '~/dependencies';
import { log } from '~/helpers';
import api from '~/services';

export enum SMSErrorCode {
  SMS_INSUFFICIENT_CREDIT = 6001,
  SMS_NO_VALID_RECIPIENTS = 6002,
  SMS_INVALID_SENDER_ID = 6003,
  SMS_IRISH_COMREG_RESTRICTED = 6004,
  SMS_USA_CAMPAIGN_NOT_APPROVED = 6005,
  SMS_DELIVERY_FAILED = 6006,
  SMS_SCHEDULED_MESSAGE_ERROR = 6007,
  SMS_BULK_SEND_PARTIAL_FAILURE = 6008,
  SMS_BULK_SEND_COMPLETE_FAILURE = 6009,
  SMS_DRAFT_MESSAGE_NOT_FOUND = 6010,
  SMS_INSUFFICIENT_LIMIT = 6011,
  SMS_ERROR = 400,
}

export enum LaravelValidatorErrorCode {
  INVALID_DATA = 42222,
}

export enum GenericErrorCode {
  UNKNOWN = 60000,
}

type LaravelValidatorError = {
  errors: Record<string, string[]>;
  message: string;
  errorCode: number;
};

type ConversationError =
  | {
      message: string;
      successCount: number;
      failedCount: number;
      totalCount: number;
      errorCode: number;
      error: SMSErrorCode;
    }
  | LaravelValidatorError
  | Record<string, any>;

interface NormalizedError {
  message: string;
  errorCode: number;
  [key: string]: any;
}

/**
 * The API is retunning the errors in different formats. This function normalizes
 * the error object to a standard structure.
 */
export function normalizeError(data: ConversationError | any): NormalizedError {
  const defaultError: NormalizedError = {
    message: `We're sorry, something went wrong. Please try again shortly.`,
    errorCode: GenericErrorCode.UNKNOWN,
  };

  if (typeof data !== 'object') return defaultError;

  if ('errorCode' in data) {
    return data as NormalizedError;
  }

  // Laravel validation errors
  if ('errors' in data) {
    return {
      ...data,
      errorCode: LaravelValidatorErrorCode.INVALID_DATA,
    };
  }

  return defaultError;
}

type NewConversationPayload = {
  channel_type: number;
  title: string | null;
  sender_id: string | null;
  is_group: boolean;
  owner_id: number;
  account_id: number;
  /**
   * @deprecated Use "contacts" or "groups" instead.
   */
  // participants?: Array<{
  //   participant_type: string;
  //   participant_id: string | number;
  //   display_name: string;
  //   is_system_user?: boolean;
  // }>;
  contacts?: string;
  groups?: string;
  message: {
    content: string;
    message_type: number;
    metadata?: {
      template_id?: number;
    };
  };
};

/**
 * Maps SMS error codes to user-friendly messages.
 */
export function getErrorMessageByCode(
  errorCode: number = GenericErrorCode.UNKNOWN
): string {
  switch (errorCode) {
    case SMSErrorCode.SMS_INSUFFICIENT_CREDIT:
      return "You don't have enough credit to send this message.";
    case SMSErrorCode.SMS_NO_VALID_RECIPIENTS:
      return 'No valid recipients were found for this message.';
    case SMSErrorCode.SMS_INVALID_SENDER_ID:
      return 'The sender ID is invalid. Please verify and try again.';
    case SMSErrorCode.SMS_IRISH_COMREG_RESTRICTED:
      return 'Message sending is restricted by Irish ComReg regulations.';
    case SMSErrorCode.SMS_USA_CAMPAIGN_NOT_APPROVED:
      return 'The SMS campaign has not been approved for the USA region.';
    case SMSErrorCode.SMS_DELIVERY_FAILED:
      return 'Message delivery failed. Please try again later.';
    case SMSErrorCode.SMS_SCHEDULED_MESSAGE_ERROR:
      return 'There was an issue with the scheduled message.';
    case SMSErrorCode.SMS_BULK_SEND_PARTIAL_FAILURE:
      return 'Some messages failed to send in this bulk operation.';
    case SMSErrorCode.SMS_BULK_SEND_COMPLETE_FAILURE:
      return 'All messages failed to send. Please review your recipients.';
    case SMSErrorCode.SMS_DRAFT_MESSAGE_NOT_FOUND:
      return 'The draft message could not be found.';
    case SMSErrorCode.SMS_INSUFFICIENT_LIMIT:
      return "You've reached your SMS limit. Please upgrade or wait for renewal.";
    case SMSErrorCode.SMS_ERROR:
      return 'No valid numbers to send to.';
    case LaravelValidatorErrorCode.INVALID_DATA:
      return 'The given data was invalid.';
    default:
      return "We're sorry, something went wrong. Please try again shortly.";
  }
}

export default function useConversation() {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<boolean>(false);

  const { account_id: accountId, userIdCode: userId } = useSelector(
    (state: RootStateOrAny) => state.auth
  );

  /**
   * Create a new conversation and returns conversationID
   */
  const create = useCallback(
    async ({
      channelType,
      contacts,
      groups,
      senderId,
      senderName,
      templateId,
      message,
    }: {
      channelType: number;
      /**
       * The list of phone number to receive the message e.g, "+3530000000,+35311111111"
       */
      contacts: string;
      /**
       * The list of group IDs to receive the message e.g, "1,2,3"
       */
      groups: string;
      senderId: string | undefined;
      senderName: string | undefined;
      templateId: number | undefined;
      message: string;
    }) => {
      try {
        if (!accountId || !userId) return;

        setLoading(true);

        const payload: NewConversationPayload = {
          channel_type: channelType,
          title: senderName || null,
          sender_id: senderId || null,
          is_group: false,
          owner_id: userId,
          account_id: accountId,
          ...(_.isString(contacts) &&
            !_.isEmpty(contacts) && {
              contacts,
            }),
          ...(_.isString(groups) && !_.isEmpty(groups) && { groups }),
          message: {
            content: message,
            message_type: 0,
            ...(_.isNumber(templateId) && {
              metadata: { template_id: templateId },
            }),
          },
        };

        const response = await api.conversation.post.createConversation(
          payload
        );

        if (response.status < 200 || response.status >= 300) {
          // Create a structured error object
          const error: any = new Error();
          error.status = response.status;
          error.data = response?.data;
          throw error;
        }

        if (response.status === 201 && response.data?.id) {
          setConversationId(response.data.id);

          setTimeout(() => {
            dispatch(actions.fetchConversationById(response.data?.id, false));
          }, 10000); // 10 seconds
        }
      } catch (err: any) {
        log.error(
          'Failed to start new conversation. Reason: ',
          JSON.stringify(err)
        );

        const errorCode = err?.data && normalizeError(err.data)?.errorCode;
        setErrorMessage(getErrorMessageByCode(errorCode));
      } finally {
        setLoading(false);
        setSubmitted(true);
      }
    },
    [setLoading, setSubmitted, setConversationId, accountId, userId]
  );

  /**
   * Add message to existing conversation using conversationID
   */
  const addMessage = useCallback(
    async (targetId, content) => {
      try {
        setLoading(true);

        const payload = {
          content,
          message_type: 0,
          is_draft: false,
          metadata: {},
        };

        const response = await api.conversation.post.addMessageToConversation(
          targetId,
          payload
        );

        if (response.status < 200 || response.status >= 300) {
          // Create a structured error object
          const error: any = new Error();
          error.status = response.status;
          error.data = response?.data;
          throw error;
        }

        dispatch(actions.fetchConversationById(targetId, false));

        setTimeout(() => {
          dispatch(actions.fetchConversationById(targetId, false));
        }, 10000); // 10 seconds
      } catch (err: any) {
        log.error(
          `Failed to add message to conversation ID ${targetId}. Reason: `,
          JSON.stringify(err)
        );

        const errorCode = err?.data && normalizeError(err?.data)?.errorCode;

        setErrorMessage(getErrorMessageByCode(errorCode));
      } finally {
        setLoading(false);
      }
    },
    [setErrorMessage, setLoading, dispatch]
  );

  return {
    errorMessage,
    loading,
    create,
    addMessage,
    setErrorMessage,
    submitted,
    conversationId,
    setSubmitted,
  };
}
